import axios from 'axios';
import { postForm } from '@/shared/api/client';
import { submitForm, submitFormData } from '@/shared/api/submit';
import { getListRows } from '@/shared/lib/pagination';
import type { SelectOption } from '@/shared/ui/selectTypes';

type CityAutocompleteItem = { id: number | string; name: string };

/** Legacy site autocomplete: city search by name fragment. */
export async function searchCitiesAutocomplete(query: string): Promise<SelectOption[]> {
  const q = query.trim();
  if (q.length <= 2) return [];
  const { data } = await axios.get<{ data?: CityAutocompleteItem[] }>(
    `${__CONFIG__.SITE_API_URL}/v2/common/city-autocomplete/`,
    { params: { city_part_name: q, is_region: true, is_country: true } },
  );
  return (data.data || []).map((item) => ({ value: String(item.id), label: item.name }));
}

export async function findCityByCoords(lat: string | number, long: string | number) {
  const body = new FormData();
  body.append('lat', String(lat));
  body.append('long', String(long));
  const { data } = await axios.post<{ city?: { id?: number | string; name?: string } | null }>(
    `${__CONFIG__.SITE_API_URL}/map/city/find-item/`,
    body,
  );
  return data.city ?? null;
}

export type AddressSuggest = { text: string; coords: [string, string] };

/** Yandex Geocoder suggest (same as legacy getAddress). */
export async function suggestAddress(query: string): Promise<AddressSuggest[]> {
  const q = query.trim();
  if (!q || !__CONFIG__.YANDEX_MAPS_API_KEY) return [];
  const { data } = await axios.get<{
    response?: {
      GeoObjectCollection?: {
        featureMember?: Array<{
          GeoObject?: {
            Point?: { pos?: string };
            metaDataProperty?: { GeocoderMetaData?: { text?: string } };
          };
        }>;
      };
    };
  }>('https://geocode-maps.yandex.ru/1.x/', {
    params: {
      apikey: __CONFIG__.YANDEX_MAPS_API_KEY,
      geocode: q,
      results: 5,
      format: 'json',
    },
  });
  const members = data.response?.GeoObjectCollection?.featureMember || [];
  return members
    .map((item) => {
      const text = item.GeoObject?.metaDataProperty?.GeocoderMetaData?.text || '';
      const pos = item.GeoObject?.Point?.pos?.split(' ') || [];
      // Yandex returns "lon lat"
      const long = pos[0] || '';
      const lat = pos[1] || '';
      return text && lat && long ? { text, coords: [lat, long] as [string, string] } : null;
    })
    .filter((item): item is AddressSuggest => Boolean(item));
}

export interface NamedItem extends Record<string, unknown> {
  id: string | number;
  name?: string;
  title?: string;
}

export async function fetchCountries() {
  const res = await postForm<Record<string, unknown>>('/place/country/list/', { page: 1, limit: 10000 });
  return getListRows<NamedItem>(res, ['countries', 'items']);
}

export async function fetchRegions(countryId: string | number) {
  if (countryId === '' || countryId === null || countryId === undefined) return [];
  const res = await postForm<Record<string, unknown>>('/place/region/list/', {
    country_id: countryId,
    limit: 10000,
  });
  return getListRows<NamedItem>(res, ['regions', 'items']);
}

export async function fetchCities(regionId: string | number) {
  if (regionId === '' || regionId === null || regionId === undefined) return [];
  const res = await postForm<Record<string, unknown>>('/place/city/list/', {
    page: 1,
    region_id: regionId,
  });
  return getListRows<NamedItem>(res, ['cities', 'items']);
}

/** Cities of a country (bids filter: region UI is hidden on test, filter by country). */
export async function fetchCitiesByCountry(countryId: string | number) {
  if (countryId === '' || countryId === null || countryId === undefined) return [];
  const res = await postForm<Record<string, unknown>>('/place/city/list/', {
    page: 1,
    country_id: countryId,
    limit: 10000,
  });
  return getListRows<NamedItem>(res, ['cities', 'items']);
}

export async function fetchRoles() {
  const res = await postForm<Record<string, unknown>>('/rbac/roles-list/', {});
  const roles = res.roles;
  if (Array.isArray(roles)) return roles as NamedItem[];
  if (roles && typeof roles === 'object') return Object.values(roles) as NamedItem[];
  return [];
}

export async function fetchUtmList() {
  const res = await postForm<Record<string, unknown>>('/utm/list/', {});
  return getListRows<NamedItem>(res, ['items', 'list']);
}

export interface UsersListParams {
  page: number;
  sort_field?: string;
  id?: string;
  name?: string;
  phone?: string;
  email?: string;
  status_id?: string;
  roles?: string[];
  country_id?: string | number;
  region_id?: string | number;
  city_id?: string | number;
  source?: string;
  partner_id?: string;
  price_template_is_parsed?: string | number;
  price_template_is_verified?: string | number;
  is_priority?: string;
  utm_id?: string;
  category?: string;
}

export function fetchUsersList(params: UsersListParams) {
  return postForm<Record<string, unknown>>('/user/profile/list/', {
    page: params.page,
    sort_field: params.sort_field || '-id',
    id: params.id,
    name: params.name,
    phone: params.phone,
    email: params.email,
    status_id: params.status_id,
    roles: params.roles,
    country_id: params.country_id,
    region_id: params.region_id,
    city_id: params.city_id,
    source: params.source,
    partner_id: params.partner_id,
    price_template_is_parsed: params.price_template_is_parsed,
    price_template_is_verified: params.price_template_is_verified,
    is_priority: params.is_priority,
    utm_id: params.utm_id,
    category: params.category,
  });
}

export async function showUserPhone(userId: string | number) {
  return postForm<{ success: number; phone?: string }>('/user/profile/show-phone/', {
    user_id: userId,
  });
}

export interface PlaceRef {
  id?: number | string;
  name?: string;
}

export interface ScheduleDay {
  name: string;
  is_active?: number | boolean;
  from?: string;
  to?: string;
}

export interface UserProfile extends Record<string, unknown> {
  id: number;
  name?: string;
  email?: string | null;
  phone?: string | null;
  contact_phone?: string | null;
  roles?: Array<{ value: string; label?: string }>;
  status_id?: number | string;
  balance?: number | string | null;
  bonus_balance?: number | string | null;
  rate?: number | string | null;
  marketing_text?: string | null;
  accept_b2b?: number | boolean | null;
  is_franchise?: number | boolean | null;
  is_cost_work_dumping_off?: number | boolean | null;
  can_work_with_autopark?: number | boolean | null;
  pay_online?: number | boolean | null;
  country?: PlaceRef | null;
  region?: PlaceRef | null;
  city?: PlaceRef | null;
  address?: string | null;
  lat?: number | string | null;
  long?: number | string | null;
  schedule_adv?: ScheduleDay[] | null;
  classification?: { value?: number | string; stars?: number | string } | number | string | null;
  category?: number | string | null;
  receive_type?: number | string | null;
  discount_level?: { level?: number | string; title?: string; max_discount_percent?: number | string } | null;
  discount_level_mode?: number | string | null;
  service_discount?: {
    discount_id?: number | string;
    discount_percent?: number | string;
    name?: string;
    max_discount_percent?: number | string;
  } | null;
  contact_person?: string | null;
  contact_person_phone?: string | null;
  is_priority?: number | boolean | null;
  priority_order?: number | string | null;
  email_notifications_enabled?: number | boolean | null;
  tg_enabled?: number | boolean | null;
  payment_method?: { type?: number | string } | null;
  contract_number?: number | string | null;
  contract_time?: number | string | null;
  group_partner?: PlaceRef | null;
  tariffs?: Array<{ name?: string; expired_time?: number | string }> | null;
  seo_data?: unknown;
}

/** `action: 'edit'` makes the server return editable fields (e.g. schedule, password flags). */
export async function fetchUserProfile(userId: string | number, isEdit = false): Promise<UserProfile> {
  const res = await postForm<{ success?: number; user?: UserProfile }>('/user/profile/view/', {
    user_id: userId,
    action: isEdit ? 'edit' : undefined,
  });
  if (res.success !== 1 || !res.user) throw new Error('User not found');
  return res.user;
}

export const createUser = (body: FormData) =>
  submitFormData<{ success?: number; user_id?: number }>('/user/profile/create/', body);

export const editUser = (body: FormData) => submitFormData('/user/profile/edit/', body);

export const editLoyaltyBalance = (userId: string | number, amount: string, comment: string) =>
  submitForm('/user/profile/edit-loyalty-balance/', {
    user_id: userId,
    amount,
    loyalty_balance_comment: comment,
  });

export async function fetchClassifications(): Promise<SelectOption[]> {
  const res = await postForm<{ classifications?: Array<{ stars: number | string; description?: string }> }>(
    '/service/get-classifications/',
  );
  return (res.classifications ?? []).map((c) => ({ value: String(c.stars), label: c.description ?? String(c.stars) }));
}

export async function fetchPartnerGroups() {
  const res = await postForm<{ data?: NamedItem[] }>('/autopark/group/list/');
  return res.data ?? [];
}

export const saveUserPartnerGroup = (groupId: string | number, userId: string | number) =>
  submitForm('/autopark/user-group/save/', { group_id: groupId, user_id: userId });

export const deleteUserPartnerGroup = (userId: string | number) =>
  submitForm('/autopark/user-group/delete/', { user_id: userId });

export type BillingOperation = 'spending' | 'refill';

const BILLING_OPERATION_TYPE: Record<BillingOperation, number> = { spending: 0, refill: 1 };

/** Billing log of one user (legacy SingleUser "История пополнений / списаний"). */
export function fetchUserBilling(userId: string | number, operation: BillingOperation, page: number) {
  return postForm<Record<string, unknown>>('/billing/billing-log/index/', {
    user_id: userId,
    operation_type: BILLING_OPERATION_TYPE[operation],
    page,
    sort_by: '-create_time',
  });
}

/** Bids of a customer, or of a service when the profile has the `service` role. */
export function fetchUserBids(userId: string | number, isService: boolean, page: number) {
  return postForm<Record<string, unknown>>('/bid/list/', {
    page,
    sort_field: '-id',
    [isService ? 'service_id' : 'user_id']: userId,
  });
}

export function fetchUserNotifications(userId: string | number, page: number) {
  return postForm<Record<string, unknown>>('/notification/get-user-notifications/', {
    user_id: userId,
    page,
  });
}

export const fineWriteOff = (userId: string | number, amount: string, comment: string) =>
  submitForm('/user/profile/fine-write-off/', {
    user_id: userId,
    debit_amount: Number(amount),
    debit_comment: comment,
  });

export const revertServiceStatus = (userId: string | number) =>
  submitForm('/user/profile/revert-service-status/', { user_id: userId });
